import React, { useEffect, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { useLanguage } from '../context/LanguageContext';
import ElevationProfile from './ElevationProfile';
import { formatNumericDate, localeFor } from '../utils/timeFormat';
import './EventRouteProfilesModal.css';

// Из «Spanish Grand Tour | Stage 4 | Race G» убираем хвост «| Race G»: гонки
// одного этапа отличаются только им, а в модалке этап показывается один раз
const RACE_SUFFIX = /\s*\|\s*Race\s+[A-Z]\s*$/i;

const geometryOf = (route) => {
  const points = route?.parameters?.simplifiedGeometryPoints;
  return Array.isArray(points) && points.length > 1 ? points : null;
};

const toNumber = (value) => {
  const number = parseFloat(value);
  return Number.isFinite(number) ? number : null;
};

// Протяженность и набор высоты маршрута — цифры ROUVY (distanceMeters, ascendedMeters).
// Набор по упрощенной геометрии не считаем: в ней десятки точек, и подъемы
// между ними теряются — выходит в разы меньше настоящего
const routeDistance = (route) => {
  const distance = toNumber(route?.parameters?.distanceMeters);
  if (distance !== null) return distance;

  const points = geometryOf(route);
  return points ? toNumber(points[points.length - 1].distance) : null;
};

const routeGain = (route) => toNumber(route?.parameters?.ascendedMeters);

// Итог по событию: суммируем только этапы, по которым цифры есть
const summarize = (stages) => stages.reduce((total, stage) => {
  const distance = routeDistance(stage.route);
  const gain = routeGain(stage.route);

  return {
    distance: total.distance + (distance || 0),
    gain: total.gain + (gain || 0),
    counted: total.counted + (distance !== null && gain !== null ? 1 : 0)
  };
}, { distance: 0, gain: 0, counted: 0 });

// Есть ли у события хотя бы один профиль трассы — от этого зависит иконка на карточке
export const hasRouteProfiles = (event) => (event?.routes || []).some(route => geometryOf(route));

// Этап — это маршрут: гонок на нем может быть несколько. Гонки собираем по route_id,
// этапы нумеруем по первому старту — так же, как расписание в PDF и протокол
const buildStages = (event) => {
  const routesById = new Map((event.routes || []).map(route => [route.id, route]));
  const racesByRoute = new Map();

  (event.event_routes || []).forEach((race) => {
    if (!routesById.has(race.route_id)) return;
    if (!racesByRoute.has(race.route_id)) racesByRoute.set(race.route_id, []);
    racesByRoute.get(race.route_id).push(race);
  });

  // Старый ответ API без route_id у гонок: этапы — просто маршруты события
  if (racesByRoute.size === 0) {
    return (event.routes || []).map(route => ({ route, name: route.name, startsAt: null }));
  }

  return Array.from(racesByRoute.entries())
    .map(([routeId, races]) => {
      const sorted = [...races].sort((a, b) => timeOf(a) - timeOf(b));
      const route = routesById.get(routeId);
      const raceName = (sorted[0].name || '').replace(RACE_SUFFIX, '').trim();

      return {
        route,
        name: raceName || route.name,
        startsAt: sorted[0].start_date_time_utc ? new Date(sorted[0].start_date_time_utc) : null
      };
    })
    .sort((a, b) => (a.startsAt ? a.startsAt.getTime() : 0) - (b.startsAt ? b.startsAt.getTime() : 0));
};

const timeOf = (race) => (race.start_date_time_utc ? new Date(race.start_date_time_utc).getTime() : 0);

const EventRouteProfilesModal = ({ event, onClose }) => {
  const { t, language } = useLanguage();
  const stages = useMemo(() => buildStages(event), [event]);
  const totals = useMemo(() => summarize(stages), [stages]);
  const locale = localeFor(language);
  const formatKm = (meters) => (meters / 1000).toLocaleString(locale, { minimumFractionDigits: 1, maximumFractionDigits: 1 });
  const formatM = (meters) => Math.round(meters).toLocaleString(locale);

  // Пока модалка открыта, листаться должна она, а не список под ней. Прокруткой
  // страницы заведует .app-content (не body), поэтому останавливаем именно его
  useEffect(() => {
    const appContent = document.querySelector('.app-content');
    const previousOverflow = appContent?.style.overflowY;
    if (appContent) appContent.style.overflowY = 'hidden';

    const handleKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKey);

    return () => {
      if (appContent) appContent.style.overflowY = previousOverflow;
      window.removeEventListener('keydown', handleKey);
    };
  }, [onClose]);

  // Портал: у карточки события при наведении есть transform, и fixed-оверлей внутри
  // нее позиционировался бы относительно карточки, а не экрана
  return ReactDOM.createPortal(
    <div className="route-profiles-overlay" onClick={onClose}>
      <div
        className="route-profiles-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="route-profiles-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="route-profiles-header">
          <div className="route-profiles-heading">
            <h2 id="route-profiles-title">{t('events.route_profiles.title')}</h2>
            <div className="route-profiles-subtitle">{event.name}</div>
          </div>
          <button
            type="button"
            className="route-profiles-close"
            onClick={onClose}
            aria-label={t('common.close')}
          >
            &times;
          </button>
        </div>

        {totals.counted > 0 && (
          <div className="route-profiles-summary">
            <div className="route-profiles-summary-item">
              <span className="route-profiles-summary-label">{t('events.route_profiles.total_distance')}</span>
              <span className="route-profiles-summary-value">{formatKm(totals.distance)} km</span>
            </div>
            <div className="route-profiles-summary-item">
              <span className="route-profiles-summary-label">{t('events.route_profiles.total_gain')}</span>
              <span className="route-profiles-summary-value">{formatM(totals.gain)} m</span>
            </div>
            <div className="route-profiles-summary-item">
              <span className="route-profiles-summary-label">{t('events.route_profiles.stages_count')}</span>
              <span className="route-profiles-summary-value">{stages.length}</span>
            </div>
            {totals.counted < stages.length && (
              <div className="route-profiles-summary-note">
                {t('events.route_profiles.partial_totals')
                  .replace('{{counted}}', totals.counted)
                  .replace('{{total}}', stages.length)}
              </div>
            )}
          </div>
        )}

        <div className="route-profiles-body">
          {stages.map((stage, index) => {
            const points = geometryOf(stage.route);

            return (
              <section key={stage.route.id} className="route-profiles-stage">
                <div className="route-profiles-stage-header">
                  <span className="route-profiles-stage-number">
                    {t('events.route_profiles.stage').replace('{{number}}', index + 1)}
                  </span>
                  <span className="route-profiles-stage-name" title={stage.name}>{stage.name}</span>
                  {stage.startsAt && (
                    <span className="route-profiles-stage-date">
                      {formatNumericDate(stage.startsAt, language)}
                    </span>
                  )}
                </div>

                {points ? (
                  <ElevationProfile
                    geometryPoints={points}
                    height={160}
                    showHeader={false}
                    gain={routeGain(stage.route)}
                  />
                ) : (
                  <div className="route-profiles-missing">{t('events.route_profiles.missing')}</div>
                )}
              </section>
            );
          })}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default EventRouteProfilesModal;
